<script setup lang="ts">
import {computed, inject, ref} from 'vue'
import {GraphDataProviderColumn, useContext, useGraphDataProvider, useLocalization} from '@hantera/portal-app'
import type {DataRow, ToolbarItem} from '@hantera/design-system'
import {DataTable, Toolbar} from '@hantera/design-system'
import type {useRoleStore} from '@/stores/iam/role-store'

const store = inject<ReturnType<typeof useRoleStore>>('roleStore')!
const t = useLocalization('iam.roles')
const context = useContext()

// Escape single quotes for Filtrera
function escapeFilter(value: string) {
  return value?.replaceAll("'", "''")
}

// Graph query configuration
const query = computed(() => ({
  edge: 'identities',
  filter: store.role ? `roles anyof ['${escapeFilter(store.role.key)}']` : 'false',
  availableFields: [
    { field: 'type', label: t('columns.type') },
    { field: 'name', label: t('columns.name') },
    { field: 'email', label: t('columns.email') },
    { field: 'identityId', label: t('columns.identityId') }
  ],
  dataFields: ['identityId', 'type'], // Always fetch these
  orderBy: 'name asc'
}))

// Columns to display
const columns = ref<GraphDataProviderColumn[]>([
  { queryPath: ['identities'], field: 'type' },
  { queryPath: ['identities'], field: 'name' },
  { queryPath: ['identities'], field: 'email' }
])

// State for filters
const providerState = ref<{ filters: Record<string, any>, orderBy?: any }>({ filters: {} })

// Create Graph data provider
const membersProvider = useGraphDataProvider(
  query,
  columns,
  providerState,
  {
    rowKey: (row) => row['identities.identityId'] as string
  }
)

// Selection state
const selectedKeys = ref<Set<string>>(new Set())

// Get identity info from row
function getIdentityFromRow(row: DataRow): { id: string, type: string, name: string } | null {
  // Access data using Graph field keys (edge.field format)
  const type = row.values[1] as string
  const name = row.values[2] as string

  if (!type) {
    console.warn('Missing type or name for identity:', row.key)
    return null
  }

  return {
    id: row.key,
    type: type,
    name: name ?? '-'
  }
}

// Context menu for each row
function createContextMenu(row: DataRow) {
  const identity = getIdentityFromRow(row)
  if (!identity) return []

  const canRemove = identity.type === 'principal'
    ? context.authorize('iam/principals:write')
    : context.authorize('iam/clients:write')

  return [
    [
      {
        label: t('view'),
        icon: 'eye',
        action: () => {
          const path = identity.type === 'principal' ? '/iam/principals' : '/iam/clients'
          context.openView(path, { query: { id: identity.id } })
        },
      },
    ],
    [
      {
        label: t('removeRole'),
        icon: 'trash',
        disabled: !canRemove,
        destructive: true,
        action: async () => {
          if (!store.role) return

          const confirmed = await store.dialogs.confirm(
            t('confirmRemoveMember', { name: identity.name, role: store.role.key }),
            t('removeRole'),
            t('remove'),
            t('cancel'),
            false
          )

          if (!confirmed) return

          try {
            if (identity.type === 'principal') {
              await store.iamService.removePrincipalRole(identity.id, store.role.key)
            } else {
              await store.iamService.removeClientRole(identity.id, store.role.key)
            }

            // Refresh the table and role store
            await membersProvider.refresh()
            await store.refresh()
            store.processes.createProcess({ description: t('memberRemoved') }).complete()
          } catch (error: any) {
            console.error('Failed to remove member:', error)
            store.processes.error({ message: t('failedToRemoveMember') })
          }
        },
      },
    ],
  ]
}

// Remove selected members
async function removeSelected() {
  if (!store.role || selectedKeys.value.size === 0) return

  const count = selectedKeys.value.size
  const confirmed = await store.dialogs.confirm(
    t('confirmRemoveMembers', { count }),
    t('removeRole'),
    t('remove'),
    t('cancel'),
    false
  )

  if (!confirmed) return

  try {
    // Get all selected rows to determine types
    const selectedIdentities = membersProvider.rows.value
      .filter(row => selectedKeys.value.has(row.key))
      .map(row => getIdentityFromRow(row))
      .filter((id): id is NonNullable<typeof id> => id !== null)

    // Remove all in parallel
    await Promise.all(
      selectedIdentities.map(identity =>
        identity.type === 'principal'
          ? store.iamService.removePrincipalRole(identity.id, store.role!.key)
          : store.iamService.removeClientRole(identity.id, store.role!.key)
      )
    )

    // Clear selection and refresh
    selectedKeys.value = new Set()
    await membersProvider.refresh()
    await store.refresh()
    store.processes.createProcess({ description: t('membersRemoved', { count }) }).complete()
  } catch (error: any) {
    console.error('Failed to remove members:', error)
    store.processes.error({ message: t('failedToRemoveMembers') })
  }
}

const toolbarItems = computed(() => {
  return [{
    type: 'button',
    id: 'remove',
    align: 'right',
    disabled: selectedKeys.value.size === 0,
    label: t('removeSelected', { count: selectedKeys.value.size }),
    buttonStyle: 'danger',
    onClick: removeSelected
  }] as ToolbarItem[]
})
</script>

<template>
  <div v-if="store.role" class="members-tab">
    <Toolbar :items="toolbarItems"/>
    <DataTable
      :provider="membersProvider"
      :context-menu="createContextMenu"
      v-model:selected="selectedKeys"
    />
  </div>

  <div v-else-if="store.isLoading" class="loading-state">
    <p>{{ t('loading') }}</p>
  </div>
</template>

<style scoped>
.members-tab {
  height: 100%;
  display: grid;
  grid-template-rows: min-content auto;
}

.loading-state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}
</style>
